import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import tenantTheme from '@theme';
import { Flex, Icon, Number as NumberDisplay, Skeleton } from '../../../../components/common';
import DrawerPopover from '../../../../components/common/drawerPopover/drawerPopover';

const barStyle = {
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'space-around',
  backgroundColor: '#F9F9F9',
  border: '1px solid #F0F0F0',
  borderRadius: 8,
  padding: '10px 8px',
  width: '100%',
  boxSizing: 'border-box',
};

const dividerStyle = {
  width: 1,
  alignSelf: 'center',
  height: 28,
  backgroundColor: '#F0F0F0',
  flexShrink: 0,
};

/** Always a finite number for display (bottom sheet + stat cells). */
const formatStatCount = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const iconWellStyle = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const STAT_STYLES = {
  Views: { bg: '#E8F5E9', icon: 'MdVisibility', color: '#2E7D32' },
  Clicks: { bg: '#FFF3E0', icon: 'MdAdsClick', color: '#E65100' },
  Leads: { bg: '#E3F2FD', icon: 'MdPhone', color: '#1565C0' },
};

/**
 * Mobile listing card stats row (Views / Clicks / Leads) — bordered bar above platform / Posted section.
 */
export const MobileListingStatsBar = ({ platforms, slug }) => {
  const { t } = useTranslation();
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const platformData = platforms?.[slug] || {};
  const { stats = {} } = platformData;
  const { views, clicks, leads } = stats;
  const leadDetails = platforms?.[slug];

  const renderValue = (stat) => {
    if (slug === 'unified' && stat === 'loading') {
      return (
        <NumberDisplay
          value={0}
          compact={false}
          style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}
        />
      );
    }
    if (stat === 'loading') return <Skeleton size="small" />;
    return (
      <NumberDisplay
        value={formatStatCount(stat)}
        compact={false}
        style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}
      />
    );
  };

  const segments = [
    { key: 'Views', label: 'Views', value: views, meta: STAT_STYLES.Views },
    { key: 'Clicks', label: 'Clicks', value: clicks, meta: STAT_STYLES.Clicks },
    {
      key: 'Leads',
      label: 'Leads',
      value: leads,
      meta: STAT_STYLES.Leads,
      leadPopover: true,
    },
  ];

  return (
    <div style={{ ...barStyle, marginBottom: 12 }}>
      {segments.map((seg, i) => (
        <React.Fragment key={seg.key}>
          {i > 0 && <div style={dividerStyle} aria-hidden />}
          <Flex
            gap="8px"
            align="center"
            style={{ flex: 1, minWidth: 0, justifyContent: 'center' }}
          >
            <span style={{ ...iconWellStyle, backgroundColor: seg.meta.bg }}>
              <Icon icon={seg.meta.icon} size="16px" color={seg.meta.color} />
            </span>
            <Flex vertical align="start" gap={2} style={{ minWidth: 0 }}>
              <Flex align="center" gap={4} wrap={false}>
                <span className="text-muted" style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                  {t(seg.label)}
                </span>
                {seg.leadPopover && (
                  <DrawerPopover
                    getTooltipContainer={null}
                    getPopupContainer={null}
                    overlayClassName="quality-popupContainer"
                    footer={null}
                    height="auto"
                    title={t('Performance')}
                    action="hover"
                    overlayStyle={{ '--width': '250px', '--spacing': '16px' }}
                    content={
                      <Flex vertical gap={!isMobile ? '0px' : '12px'}>
                        <Flex justify="space-between">
                          <span>{t('Calls')}</span>
                          <span>{formatStatCount(leadDetails?.calls)}</span>
                        </Flex>
                        <Flex justify="space-between">
                          <span>{t('Whatsapp')}</span>
                          <span>{formatStatCount(leadDetails?.whatsapp)}</span>
                        </Flex>
                        <Flex justify="space-between">
                          <span>{t('SMS')}</span>
                          <span>{formatStatCount(leadDetails?.sms)}</span>
                        </Flex>
                        <Flex justify="space-between">
                          <span>{t('Email')}</span>
                          <span>{formatStatCount(leadDetails?.emails)}</span>
                        </Flex>
                      </Flex>
                    }
                  >
                    <Icon icon="FaCaretDown" size="14px" color={tenantTheme['primary-color']} />
                  </DrawerPopover>
                )}
              </Flex>
              {renderValue(seg.value)}
            </Flex>
          </Flex>
        </React.Fragment>
      ))}
    </div>
  );
};

export default MobileListingStatsBar;
